import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './Spinner';
import logger from '../utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireOnboarding = false
}) => {
  const { user, isLoading, needsOnboarding, authError, refreshOnboardingStatus } = useAuth();

  useEffect(() => {
    logger.componentMount('ProtectedRoute', { requireOnboarding, hasUser: !!user, isLoading, needsOnboarding, hasError: !!authError });
    return () => {
      logger.componentUnmount('ProtectedRoute');
    };
  }, [requireOnboarding, user, isLoading, needsOnboarding, authError]);

  if (isLoading) {
    logger.debug('ProtectedRoute', 'Auth loading, showing spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authError) {
    logger.warn('ProtectedRoute', 'Auth error detected', { error: authError });
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center">
        <div className="max-w-md w-full glass-grain rounded-2xl p-8 border border-red-500/30 bg-red-500/10 backdrop-blur-xl">
          <h2 className="text-xl font-heading text-red-400 mb-4">Connection Error</h2>
          <p className="text-text-secondary mb-6">{authError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/50 transition-all duration-300"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    logger.warn('ProtectedRoute', 'No user found, redirecting to landing', { requireOnboarding });
    return <Navigate to="/" replace />;
  }

  if (requireOnboarding && needsOnboarding) {
    logger.info('ProtectedRoute', 'Onboarding required but not complete, redirecting to onboarding', { userEmail: user.email });
    return <Navigate to="/onboarding" replace />;
  }

  logger.debug('ProtectedRoute', 'Access granted', { userEmail: user.email, requireOnboarding, needsOnboarding });
  return <>{children}</>;
};

export default ProtectedRoute;

